import React, { useState, useEffect, useRef } from "react";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonModal,
    IonContent,
} from "@ionic/react";
import { Helmet } from "react-helmet";

// Define question types
type QuestionType = "picture" | "identifyOddOneOut";

interface PictureQuestion {
    question: string;
    options: { src: string; answer: boolean }[];
}

interface IdentifyOddOneOutQuestion {
    question: string;
    options: string[];
    correct: string;
}



const AnimalQuiz: React.FC = () => {
    const [currentQuestion, setCurrentQuestion] = useState<
        PictureQuestion | IdentifyOddOneOutQuestion | null
    >(null);
    const [selectedOption, setSelectedOption] = useState<string | boolean | null>(null);
    const [isCorrect, setIsCorrect] = useState(false);
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const [questionBank, setQuestionBank] = useState<Record<QuestionType, (PictureQuestion | IdentifyOddOneOutQuestion)[]>>({
        picture: [],
        identifyOddOneOut: []
    });

    const fetchQuestions = async () => {
        try {
            let identifyOddOneOutQuestion: IdentifyOddOneOutQuestion[] = [];
            let pictureQuestionQuestion: PictureQuestion[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {

                const response2 = await fetch(process.env.REACT_APP_API_URL + 'AnimalIdentifyOddOneOut.json');
                identifyOddOneOutQuestion = await response2.json();

                const response4 = await fetch(process.env.REACT_APP_API_URL + 'AnimalPictureQuestion.json');
                pictureQuestionQuestion = await response4.json();

            } else {

                const module2 = await import('../../../datas/AnimalIdentifyOddOneOut.json');
                identifyOddOneOutQuestion = module2.default;

                const module4 = await import('../../../datas/AnimalPictureQuestion.json');
                pictureQuestionQuestion = module4.default;
            }
            setQuestionBank({
                picture: pictureQuestionQuestion,
                identifyOddOneOut: identifyOddOneOutQuestion
            });

            getRandomQuestion();
        } catch (error) {
            console.error("Error loading questions:", error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Animal Worksheets for LKG | Wild, Domestic & Pet Animals | Mama’s Guidance"; // Set page title
    }, []);

    useEffect(() => {
        if (questionBank.identifyOddOneOut.length) {
            getRandomQuestion();
        }
    }, [questionBank]);

    const getRandomQuestion = () => {
        const questionTypes = Object.keys(questionBank) as QuestionType[];
        const randomType = questionTypes[Math.floor(Math.random() * questionTypes.length)];
        const randomQuestion = questionBank[randomType][Math.floor(Math.random() * questionBank[randomType].length)];
        setCurrentQuestion(randomQuestion);
        setSelectedOption(null);
    };

    const handleAnswerSubmit = (option: any) => {
        setSelectedOption(option);
        if (!currentQuestion) return;

        if ("correct" in currentQuestion) {
            if (option === currentQuestion.correct) {
                showNextQuestion();
                setIsCorrect(true);
            }
        } else if ("options" in currentQuestion) {
            // For picture-based questions
            if ((currentQuestion as PictureQuestion).options.find((opt) => opt.src === option)?.answer) {
                showNextQuestion();
                setIsCorrect(true);
            }
        }
    };

    const showNextQuestion = () => {
        setShowImage(true);
        setTimeout(() => {
            setShowImage(false);
            setSelectedOption(null);
            getRandomQuestion();
            setIsCorrect(false)
        }, 5000);
    }

    return (
        <>
            <Helmet>
                <meta name="description" content="Explore fun and interactive animal worksheets for LKG kids. Learn about wild, domestic, and pet animals with matching, coloring, and activity sheets." />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Explore the Animal World</h1>

                                <p>
                                    Children love animals — and learning about them is both fun and educational! This collection of <strong>LKG-level animal worksheets</strong> introduces young learners to <strong>wild animals, domestic animals, and pet animals</strong>. Through matching, identifying, coloring, and classification activities, your child will build vocabulary, observation skills, and general awareness.
                                </p>

                                <h2>🦁 Types of Animals Covered</h2>
                                <ul>
                                    <li><strong>Wild Animals:</strong> Lion, Elephant, Tiger, Giraffe, Zebra</li>
                                    <li><strong>Domestic Animals:</strong> Cow, Goat, Horse, Sheep</li>
                                    <li><strong>Pet Animals:</strong> Dog, Cat, Rabbit, Parrot</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify different types of animals</li>
                                    <li>Classify animals into wild, domestic, and pet groups</li>
                                    <li>Recognize common animals through pictures</li>
                                    <li>Build early environmental science vocabulary</li>
                                </ul>

                                <h2>📋 What&apos;s Included in the Worksheets?</h2>
                                <ul>
                                    <li>Match animal to its name or category</li>
                                    <li>Circle the correct animal</li>
                                    <li>Identify animals from pictures</li>
                                    <li>Color your favorite pet</li>
                                    <li>Draw lines to group animals</li>
                                </ul>

                                <h2>👩‍🏫 Teaching Tips for Parents & Educators</h2>
                                <ul>
                                    <li>Show animal flashcards and ask your child to name and classify them</li>
                                    <li>Visit a zoo or watch animal videos to make the lesson real and exciting</li>
                                    <li>Use soft toys or animal figurines to play sorting games at home</li>
                                    <li>Encourage storytelling with animals: “What does the cat do?” “Where does a lion live?”</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/environmental-science/living-non-living"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Living & Non Living Things</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/environmental-science/traffic-rules-safety"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Traffic Rules & Safety</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Children connect best with animals through visuals and stories. Make animal learning part of their everyday play.
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Explore the Animal World</h1>

                            <p>
                                Children love animals — and learning about them is both fun and educational! This collection of <strong>LKG-level animal worksheets</strong> introduces young learners to <strong>wild animals, domestic animals, and pet animals</strong>. Through matching, identifying, coloring, and classification activities, your child will build vocabulary, observation skills, and general awareness.
                            </p>

                            <h2>🦁 Types of Animals Covered</h2>
                            <ul>
                                <li><strong>Wild Animals:</strong> Lion, Elephant, Tiger, Giraffe, Zebra</li>
                                <li><strong>Domestic Animals:</strong> Cow, Goat, Horse, Sheep</li>
                                <li><strong>Pet Animals:</strong> Dog, Cat, Rabbit, Parrot</li>
                            </ul>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Identify different types of animals</li>
                                <li>Classify animals into wild, domestic, and pet groups</li>
                                <li>Recognize common animals through pictures</li>
                                <li>Build early environmental science vocabulary</li>
                            </ul>

                            <h2>📋 What&apos;s Included in the Worksheets?</h2>
                            <ul>
                                <li>Match animal to its name or category</li>
                                <li>Circle the correct animal</li>
                                <li>Identify animals from pictures</li>
                                <li>Color your favorite pet</li>
                                <li>Draw lines to group animals</li>
                            </ul>

                            <h2>👩‍🏫 Teaching Tips for Parents & Educators</h2>
                            <ol>
                                <li>Show animal flashcards and ask your child to name and classify them</li>
                                <li>Visit a zoo or watch animal videos to make the lesson real and exciting</li>
                                <li>Use soft toys or animal figurines to play sorting games at home</li>
                                <li>Encourage storytelling with animals: “What does the cat do?” “Where does a lion live?”</li>
                            </ol>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/lkg/environmental-science/living-non-living"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Living & Non Living Things</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/environmental-science/traffic-rules-safety"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Traffic Rules & Safety</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Children connect best with animals through visuals and stories. Make animal learning part of their everyday play.
                            </p>
                        </article>
                        {currentQuestion ? (
                            <>
                                <h3 className="mb-2">{currentQuestion.question}</h3>

                                {"options" in currentQuestion ? (
                                    // Handle multiple-choice or identify odd one out
                                    typeof currentQuestion.options[0] === "string" ? (
                                        <IonRadioGroup onIonChange={(e) => handleAnswerSubmit(e.detail.value)}>
                                            {(currentQuestion as IdentifyOddOneOutQuestion).options.map((option, index) => (
                                                <IonItem key={index}
                                                    className={`grid-item ${selectedOption === option
                                                        ? isCorrect === true
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}>
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonRadioGroup>
                                    ) : (
                                        // Handle picture-based questions
                                        <div className="row">
                                            {(currentQuestion as PictureQuestion).options.map((option, index) => (
                                                <div
                                                    className={`col-6 col-md-3 mb-5 `}
                                                    key={index}>
                                                    <img
                                                        src={process.env.REACT_APP_API_URL + option.src}
                                                        onClick={() => handleAnswerSubmit(option.src)}
                                                        className={`livingImage img-thumbnail ${selectedOption === option.src
                                                            ? isCorrect === true
                                                                ? "correct"
                                                                : "errorImgItems"
                                                            : ""
                                                            }`}
                                                        style={{
                                                            cursor: "pointer"
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )
                                ) : (
                                    // Handle True/False questions
                                    <>
                                        <IonRadioGroup onIonChange={(e) => handleAnswerSubmit(e.detail.value!)}>
                                            <IonItem
                                                className={`grid-item ${selectedOption === true
                                                    ? isCorrect === true
                                                        ? "correct"
                                                        : "errorItems"
                                                    : ""
                                                    }`}>
                                                <IonRadio slot="start" value={true} />
                                                <IonLabel>True</IonLabel>
                                            </IonItem>
                                            <IonItem
                                                className={`grid-item ${selectedOption === false
                                                    ? isCorrect === true
                                                        ? "correct"
                                                        : "errorItems"
                                                    : ""
                                                    }`}>
                                                <IonRadio slot="start" value={false} />
                                                <IonLabel>False</IonLabel>
                                            </IonItem>
                                        </IonRadioGroup>
                                    </>
                                )}
                            </>
                        ) : (
                            <p>Loading question...</p>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default AnimalQuiz;
